import { createFileRoute } from "@tanstack/react-router"
import { useState } from "react"

import { Switch } from "~/components/design-system/components"
import { AnalysisPage } from "~/components/design-system/pages"
import { FallingOff, NeverAsked, PowerUsers } from "~/components/internal/haki-usage/cohorts"
import { AdoptionShape, ActivityChart, TopicBank } from "~/components/internal/haki-usage/charts"
import { Section, UsageCard } from "~/components/internal/haki-usage/cards"
import { UsageSummaryCards } from "~/components/internal/haki-usage/SummaryCards"
import { UsageTable } from "~/components/internal/haki-usage/UsageTable"
import { rangeOptions } from "~/components/internal/haki-usage/shared"
import SearchField from "~/components/planning/SearchField"
import InfoTooltip from "~/components/reusable/InfoTooltip"
import { Label } from "~/components/reusable/Label"
import Select from "~/components/reusable/Select"
import { mergeTopicTallies } from "~/server/api/routers/haki/usage-adoption"
import { api } from "~/utils/api"
import { groupBy } from "~/utils/tsUtils"

export const Route = createFileRoute("/_auth/internal/customer-success/_layout/haki-usage")({
  component: RouteComponent,
})

function RouteComponent() {
  const [range, setRange] = useState(rangeOptions[1]!)
  const [excludeStaff, setExcludeStaff] = useState(true)
  const [search, setSearch] = useState("")
  const [expandedOrgs, setExpandedOrgs] = useState<Set<string>>(new Set())

  const {
    data,
    isLoading,
    isError: isUsageStatsError,
  } = api.haki.getUsageStats.useQuery({ daysBack: range.value, excludeStaff })

  const byOrg = data?.byOrg ?? []
  const orgIdsWithQuestions = byOrg.filter((r) => r.selfDrivenTurns > 0).map((r) => r.orgId)

  const { data: topicsByOrg, isLoading: topicsLoading } = api.haki.getUsageTopics.useQuery(
    { orgIds: orgIdsWithQuestions, daysBack: range.value, excludeStaff },
    { enabled: orgIdsWithQuestions.length > 0 },
  )

  const active = byOrg.filter((r) => r.turns > 0)
  const totalTurns = byOrg.reduce((sum, r) => sum + Number(r.turns), 0)
  const selfDrivenTurns = byOrg.reduce((sum, r) => sum + r.selfDrivenTurns, 0)

  const usersByOrg = groupBy(data?.byUser ?? [], "orgId")
  const topicBank = mergeTopicTallies(Object.values(topicsByOrg ?? {}).map((t) => t.categories))

  function toggleOrg(orgId: string) {
    setExpandedOrgs((prev) => {
      const next = new Set(prev)
      if (next.has(orgId)) next.delete(orgId)
      else next.add(orgId)
      return next
    })
  }

  const query = search.trim().toLowerCase()
  const visibleOrgs = query ? byOrg.filter((r) => r.orgId.toLowerCase().includes(query)) : byOrg

  return (
    <AnalysisPage>
      <div className="flex flex-col gap-8">
        <h1 className="heading-md text-fg-base">Haki usage &amp; adoption</h1>

        <div className="flex items-end gap-6 flex-wrap">
          <div className="flex flex-col gap-2">
            <Label className="font-bold">Time range</Label>
            <Select
              options={rangeOptions}
              value={range}
              onChange={(v) => {
                if (v) {
                  setRange(v)
                  setExpandedOrgs(new Set())
                }
              }}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label className="font-bold">Find a company</Label>
            <SearchField
              initialSearchText={search}
              onSearchTextChange={setSearch}
              placeholder="Filter by org id"
            />
          </div>
          <div className="flex items-center gap-2 pb-2">
            <Switch checked={excludeStaff} onCheckedChange={setExcludeStaff} />
            <Label>Hide Hakio staff</Label>
            <InfoTooltip
              title="Hide Hakio staff"
              description="Excludes anyone with a @hakio.com email or a Hakio staff role in Auth0."
              side="top"
              containerClassName="max-w-[50ch]"
            />
          </div>
        </div>

        {isUsageStatsError ? (
          <p className="paragraph-base text-fg-subtle">Could not load usage data. Try reloading.</p>
        ) : (
          <>
            <Section title="Current customer trends">
              <div className="grid grid-cols-3 gap-4 items-start">
                <FallingOff byOrg={byOrg} topicsByOrg={topicsByOrg} isLoading={isLoading} />
                <NeverAsked byOrg={byOrg} topicsByOrg={topicsByOrg} isLoading={isLoading} />
                <PowerUsers byOrg={byOrg} topicsByOrg={topicsByOrg} isLoading={isLoading} />
              </div>
            </Section>

            <Section title="Every company">
              <UsageTable
                isLoading={isLoading}
                byOrg={visibleOrgs}
                usersByOrg={usersByOrg}
                topicsByOrg={topicsByOrg}
                topicsLoading={topicsLoading}
                expandedOrgs={expandedOrgs}
                onToggleOrg={toggleOrg}
                daysBack={range.value}
                excludeStaff={excludeStaff}
                emptyMessage={
                  query
                    ? `No company matches "${search.trim()}".`
                    : "No company used Haki in this period."
                }
              />
            </Section>

            <Section title="Adoption overall">
              <div className="flex flex-col gap-4">
                <UsageSummaryCards
                  isLoading={isLoading}
                  selfDrivenTurns={selfDrivenTurns}
                  totalTurns={totalTurns}
                  companiesCount={active.length}
                  askingCount={orgIdsWithQuestions.length}
                  staffExcluded={data?.staffExcluded ?? false}
                  staffTurns={data?.staffTurns ?? 0}
                  harnessTurns={data?.harnessTurns ?? 0}
                />

                <UsageCard
                  title="Activity over time"
                  isLoading={isLoading}
                  skeletonHeight="h-[248px]"
                >
                  <ActivityChart daily={data?.daily ?? []} height={200} />
                </UsageCard>

                <div className="grid grid-cols-2 gap-4 items-start">
                  <AdoptionShape byOrg={active} isLoading={isLoading} />
                  <TopicBank topics={topicBank} isLoading={isLoading || topicsLoading} />
                </div>
              </div>
            </Section>
          </>
        )}
      </div>
    </AnalysisPage>
  )
}
